/* ClientAvatar — square portrait tile shown in the Client Sheet hero.

  Two modes:
   - Read-only display (no `onUpload`): renders the square avatar with the
     resolved portrait OR a champagne-gold initials fallback. Used in
     listings / kanban / calendar cards where the user can't change the
     photo from that surface.
   - Interactive uploader (`onUpload` provided): the tile becomes a
     click/tap target that opens the native file picker, validates the
     file client-side, and calls `onUpload(dataUrl)` with the result.
     Parent decides whether to PUT the data URL to
     ``/api/clients/{id}/profile-image`` or store inline.

  Priority order for which image we render (matches the backend
  resolver in `_resolve_client_images`):
    1. client.profile_image  ← uploaded via this very component
    2. client.cover_image
    3. client.photos[0]      ← legacy + makeup/hair sections
    4. champagne-gold initials fallback

  Why an in-component <input type="file"> hidden ref:
   - Native picker triggers the system camera roll on mobile, which
     is the exact UX requested ("tap → choose photo from gallery").
   - We don't need drag-and-drop here; the Hero is a single small
     target and the file input keeps the implementation tiny.
*/
import React, { useMemo, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";

const SIZE_CLASSES = {
  sm: "w-10 h-10 text-xs",
  md: "w-14 h-14 text-sm",
  lg: "w-20 h-20 text-base",
  // Hero (legacy): 72 mobile → 80 tablet → 96 desktop.
  xl: "w-[72px] h-[72px] sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-lg",
  // Hero "premium CRM" — molto più grande, l'avatar diventa un
  // elemento importante della scheda cliente.
  // 100 mobile → 130 tablet (sm) → 160 desktop (lg).
  hero: "w-[100px] h-[100px] sm:w-[130px] sm:h-[130px] lg:w-[160px] lg:h-[160px] text-2xl sm:text-3xl lg:text-4xl",
};

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif";
// Match backend: 8 MB base64 ≈ 5.8 MB binary. We keep some headroom.
const MAX_BYTES = 5.5 * 1024 * 1024;

/** Compute up to 2 uppercase initials from any human name. */
function deriveInitials(client) {
  const name = (client?.name || "").trim();
  const partner = (client?.partner_name || "").trim();
  const tokens = name.split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) return (tokens[0][0] + tokens[1][0]).toUpperCase();
  if (tokens.length === 1) {
    if (partner) return (tokens[0][0] + partner.split(/\s+/)[0][0]).toUpperCase();
    return tokens[0].slice(0, 2).toUpperCase();
  }
  return "•";
}

/** Best portrait URL in priority order. Returns "" when nothing is set
 *  so the caller can render the initials fallback. */
function pickPortrait(client) {
  if (!client) return "";
  const candidates = [
    client.profile_image,   // explicit avatar — top priority (uploaded here)
    client.cover_image,
    Array.isArray(client.photos) ? client.photos[0] : null,
    client.bride_photo,
    client.avatar_url,
  ];
  for (const raw of candidates) {
    if (typeof raw === "string" && raw.trim()) return raw.trim();
    if (raw && typeof raw === "object" && typeof raw.url === "string" && raw.url.trim()) {
      return raw.url.trim();
    }
  }
  return "";
}

/** Read a File → data URL (base64). Used so the parent can POST a
 *  single JSON payload — same pattern as the branding logo upload. */
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

export default function ClientAvatar({
  client,
  size = "md",
  className = "",
  /** When provided, the tile becomes clickable. Receives the base64
   *  data URL of the chosen file. Should return a Promise so we can
   *  show a spinner while the upload completes. */
  onUpload = null,
}) {
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const portrait = useMemo(() => pickPortrait(client), [client]);
  const initials = useMemo(() => deriveInitials(client), [client]);

  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Cache-buster so the <img> reloads after a successful upload even
  // when the URL path stays the same (e.g. R2 reused the same key).
  const [bust, setBust] = useState(0);

  const interactive = typeof onUpload === "function";

  const handlePick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";  // allow re-uploading the same file later
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      // Surface a soft warning — parent decides whether to toast.
      onUpload && onUpload(null, new Error("Formato non valido: scegli un'immagine."));
      return;
    }
    if (file.size > MAX_BYTES) {
      onUpload && onUpload(null, new Error("Immagine troppo grande (max 5 MB)."));
      return;
    }
    try {
      setBusy(true);
      const dataUrl = await readAsDataUrl(file);
      await onUpload(dataUrl);
      setBust((n) => n + 1);
    } catch (err) {
      // Re-throw so the parent's toast layer can also catch it if it
      // wrapped the call with its own try/catch.
      console.error("[ClientAvatar] upload failed", err);
    } finally {
      setBusy(false);
    }
  };

  const baseWrapper =
    `relative group inline-flex items-center justify-center shrink-0 overflow-hidden ` +
    `rounded-3xl bg-gradient-to-br from-[#D6B47A] via-[#C8A46A] to-[#A6824A] ` +
    `text-white font-serif-heading tracking-wide ` +
    `ring-1 ring-[#D6C3A1]/30 shadow-xl shadow-[#C8A46A]/20 ` +
    `border-[4px] border-white ${sizeCls} ${className}`;
  const wrapperCls = interactive
    ? baseWrapper + " cursor-pointer transition-shadow hover:shadow-xl"
    : baseWrapper;

  const renderInner = () => {
    if (portrait) {
      // Append a cache-buster only when we've just uploaded a new file —
      // skip on first paint so static URLs benefit from browser caching.
      const src = bust > 0 && portrait.includes("?") ? `${portrait}&b=${bust}` : (bust > 0 ? `${portrait}?b=${bust}` : portrait);
      return (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          data-testid="client-avatar-img"
        />
      );
    }
    // Initials + tiny caption when the picker is enabled. When the avatar
    // is read-only we just show the initials (no caption clutter).
    return (
      <span className="flex flex-col items-center justify-center leading-tight px-1">
        <span className="select-none" data-testid="client-avatar-initials">{initials}</span>
        {interactive && (
          <span className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-wider opacity-90 flex items-center gap-1">
            <Camera className="w-3 h-3" strokeWidth={1.5} />
            Aggiungi foto
          </span>
        )}
      </span>
    );
  };

  // Plain, read-only render — no input, no overlay.
  if (!interactive) {
    return (
      <span
        className={wrapperCls}
        aria-label={client?.name || "Cliente"}
        data-testid="client-avatar"
      >
        {renderInner()}
      </span>
    );
  }

  // Interactive uploader. Use a <button> for proper a11y/keyboard support.
  return (
    <button
      type="button"
      className={wrapperCls}
      onClick={() => !busy && inputRef.current?.click()}
      aria-label={portrait ? "Cambia foto cliente" : "Aggiungi foto cliente"}
      data-testid="client-avatar"
      disabled={busy}
    >
      {renderInner()}

      {/* Hover/tap overlay — visible on hover (desktop) and active (mobile tap). */}
      <span
        className={
          "absolute inset-0 flex items-center justify-center bg-black/45 text-white text-xs sm:text-sm font-medium tracking-wide " +
          "opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-150 pointer-events-none"
        }
        data-testid="client-avatar-overlay"
      >
        <span className="flex items-center gap-1.5">
          <Camera className="w-4 h-4" strokeWidth={1.5} />
          {portrait ? "Cambia foto" : "Aggiungi foto"}
        </span>
      </span>

      {/* Busy spinner — replaces the overlay while uploading. */}
      {busy && (
        <span
          className="absolute inset-0 flex items-center justify-center bg-black/55 text-white"
          data-testid="client-avatar-spinner"
        >
          <Loader2 className="w-7 h-7 animate-spin" strokeWidth={1.5} />
        </span>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={handlePick}
        data-testid="client-avatar-input"
      />
    </button>
  );
}
